'use client';

import Link from 'next/link';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTheme } from './ThemeProvider';
import { useMenu } from './MenuProvider';
import { writeOfflinePrefs } from '@/lib/offlinePrefsMirror';
import SearchBar from './SearchBar';

function IconFeed({ active }: { active: boolean }) {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={active ? 2 : 1.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}

function IconPin({ active }: { active: boolean }) {
  // Lucide Pin (filled when active, outline otherwise). Заменяет старый
  // дартс-таргет — pin семантически точнее: закреп = выделил из массы,
  // а не "целюсь сюда". И консистентно с TG-метафорой "закрепить сообщение".
  return (
    <svg className="w-5 h-5" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={active ? 2 : 1.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
    </svg>
  );
}

function IconLists({ active }: { active: boolean }) {
  return (
    <svg className="w-5 h-5" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={active ? 2 : 1.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function IconSettings({ active }: { active: boolean }) {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={active ? 2 : 1.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function IconSun() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function IconMoon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" viewBox="0 0 24 24">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

// «Настройки» больше не пункт нав-бара — переехали в шапку шестерёнкой.
// «Лента» есть всегда; «Закреп» и «Мои списки» показываются только если
// включены в настройках («Меню приложения»). См. MenuProvider / menuVisibility.
const FEED_ITEM   = { href: '/dashboard',          label: 'Лента',      Icon: IconFeed  };
const PINNED_ITEM = { href: '/dashboard?pinned=1', label: 'Закреп',     Icon: IconPin   };
const LISTS_ITEM  = { href: '/hidden',             label: 'Мои списки', Icon: IconLists };

export default function Navbar({ firstName }: { firstName?: string }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { theme, toggle } = useTheme();
  const menu = useMenu();
  // useSearchParams может вернуть null во время RSC-стриминга
  const isPinnedView = searchParams?.get('pinned') === '1';

  // Зеркало имени для шапки офлайн-страниц (вход в настройки, как здесь).
  useEffect(() => {
    writeOfflinePrefs({ firstName: firstName || null });
  }, [firstName]);

  // Динамический список разделов. «Лента» всегда; остальные — по настройкам.
  const navItems = [
    FEED_ITEM,
    ...(menu.sectionPinned ? [PINNED_ITEM] : []),
    ...(menu.sectionLists  ? [LISTS_ITEM]  : []),
  ];
  // Бар из одной «Ленты» бессмыслен — на мобиле прячем его целиком, на
  // десктопе не рисуем центральную группу.
  const showNavGroup = navItems.length > 1;
  const settingsActive = pathname.startsWith('/settings');
  // Если в центре шапки нет пунктов меню — на десктопе используем это место
  // под поиск (только на ленте дашборда, не в pinned-view). search-accessible
  // (ui-ux-pro-max): поиск удобнее в постоянной верхней панели.
  const showHeaderSearch = !showNavGroup && pathname === '/dashboard' && !isPinnedView;

  function isActive(href: string) {
    if (href === '/hidden')   return pathname.startsWith('/hidden');
    // «Закреп» — это виртуальный фильтр на dashboard. Активен только при
    // ?pinned=1. «Лента» — наоборот, без него.
    if (href === '/dashboard?pinned=1') return pathname === '/dashboard' && isPinnedView;
    if (href === '/dashboard')          return pathname === '/dashboard' && !isPinnedView;
    return pathname === href;
  }

  return (
    <>
      {/* Top header */}
      <header className="sticky top-0 z-50 bg-frosted backdrop-blur-sm border-b border-line">
        {/* Три зоны: логотип к левому краю, разделы — центрированной группой по
            центру страницы, юзер+тема к правому. Меню — связанная группа с умеренным
            постоянным интервалом (gap-6 ≈ 24px), а не растяжка по краям (по
            ui-ux-pro-max: full-width justify рассыпает связанные пункты). Боковые
            1fr равны → группа page-centered, ровно над лентой. Зоны закреплены за
            колонками (col-start-1/2/3) — иначе при скрытом на мобиле меню grid
            кидал бы правый блок в центр. На мобиле центр пуст (md:hidden nav). */}
        <div className="px-4 md:px-6 h-14 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          {/* Logo */}
          <Link href="/dashboard" className="col-start-1 flex items-center gap-2.5 cursor-pointer justify-self-start min-w-0">
            <div data-launch-target className="w-7 h-7 rounded-lg bg-accent flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-accent-fg" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <span className="font-semibold text-primary text-sm tracking-tight truncate">Favbox</span>
          </Link>

          {/* Центральная зона (десктоп). Приоритет: пункты меню → иначе на
              ленте кладём сюда поиск → иначе пусто. */}
          <div className="col-start-2 hidden md:flex items-center justify-self-center min-w-0">
            {showNavGroup ? (
              <nav className="flex items-center gap-6">
                {navItems.map(({ href, label, Icon }) => {
                  const active = isActive(href);
                  return (
                    <Link
                      key={href}
                      href={href}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors cursor-pointer ${
                        active
                          ? 'bg-accent text-accent-fg'
                          : 'text-secondary hover:text-primary hover:bg-surface'
                      }`}
                    >
                      <Icon active={active} />
                      <span>{label}</span>
                    </Link>
                  );
                })}
              </nav>
            ) : showHeaderSearch ? (
              // Ширину равняем под контентную колонку ленты (max-w-3xl = 48rem),
              // адаптивный cap по vw — чтобы на узких десктопах оставалось место
              // под логотип слева и настройки/тему справа.
              <div className="w-[48rem] max-w-[46vw]">
                <SearchBar />
              </div>
            ) : null}
          </div>

          {/* Right side: имя пользователя + шестерёнка (оба ведут в Настройки)
              + переключатель темы. Прижаты к правому краю. */}
          <div className="col-start-3 flex items-center gap-1 justify-self-end min-w-0">
            <Link
              href="/settings"
              aria-label="Настройки"
              aria-current={settingsActive ? 'page' : undefined}
              className={`flex items-center gap-1.5 h-8 pl-2 pr-2 rounded-lg transition-colors shrink-0 min-w-0 cursor-pointer ${
                settingsActive
                  ? 'bg-accent text-accent-fg'
                  : 'text-secondary hover:text-primary hover:bg-surface'
              }`}
            >
              {firstName && (
                <span className={`text-xs truncate max-w-[6rem] ${settingsActive ? 'text-accent-fg' : 'text-tertiary'}`}>{firstName}</span>
              )}
              <IconSettings active={settingsActive} />
            </Link>

            {/* Light/dark toggle */}
            <button
              onClick={toggle}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-secondary hover:text-primary hover:bg-surface transition-colors shrink-0"
              title={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            >
              {theme === 'dark' ? <IconSun /> : <IconMoon />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile bottom navigation. Прячется целиком, если осталась одна «Лента»
          (оба раздела выключены) — тогда низ освобождается под кнопку «+».
          При 2 пунктах центрируем группу с интервалом, чтобы не разъезжалась
          по краям; при 3 — равномерно. */}
      {showNavGroup && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-frosted backdrop-blur-sm border-t border-line">
          <div className={`flex items-stretch h-16 max-w-md mx-auto ${navItems.length >= 3 ? 'justify-around' : 'justify-center gap-12'}`}>
            {navItems.map(({ href, label, Icon }) => {
              const active = isActive(href);
              return (
                <Link
                  key={href}
                  href={href}
                  className={`flex flex-col items-center justify-center gap-1 transition-colors cursor-pointer ${
                    navItems.length >= 3 ? 'flex-1' : 'w-20'
                  } ${active ? 'text-accent' : 'text-tertiary hover:text-secondary'}`}
                >
                  <Icon active={active} />
                  <span className="text-[10px] font-medium leading-none">{label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </>
  );
}
